import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../components/auth/AuthContext";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Paper,
  Grid,
  DialogContentText,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import {
  ExpandMore,
  CalendarToday,
  School,
  TrendingUp,
  Assessment,
  ArrowUpward,
  Groups,
  SchoolOutlined,
  HistoryEdu,
} from "@mui/icons-material";
import axios from "axios";

const StudentDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { learner } = location.state || {};
  const { schoolId: contextSchoolId } = useAuth();
  const schoolId = learner?.schoolId || contextSchoolId;

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [birthCertInput, setBirthCertInput] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    fullName: learner?.fullName || "",
    dateOfBirth: learner?.dateOfBirth ? learner.dateOfBirth.split("T")[0] : "",
    className: learner?.class || "",
    stream: learner?.stream || "",
    admissionNumber: learner?.admissionNumber || "",
    birthCertificateNumber: learner?.birthCertificateNumber || "",
    gender: learner?.gender || "",
    parentName: learner?.parentName || "",
    parentContact: learner?.parentContact || "",
    parentEmail: learner?.parentEmail || "",
    parentId: learner?.parentId || "",
  });
  const [schoolStreams, setSchoolStreams] = useState({});
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [expandedYears, setExpandedYears] = useState({});

  // Promotion states
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [promotionType, setPromotionType] = useState("individual");
  const [promotionStep, setPromotionStep] = useState(0);
  const [promotionData, setPromotionData] = useState({
    academicYear: new Date().getFullYear().toString(),
    newClass: "",
    newStream: "",
    remarks: "",
    finalGrade: "",
    furtherEducation: {
      institution: "",
      course: "",
      yearStarted: "",
      qualification: "",
    },
    currentOccupation: {
      company: "",
      position: "",
      industry: "",
      yearStarted: "",
    },
  });
  const [promoting, setPromoting] = useState(false);
  const [academicHistory, setAcademicHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const classes = [
    "PP1",
    "PP2",
    "Grade 1",
    "Grade 2",
    "Grade 3",
    "Grade 4",
    "Grade 5",
    "Grade 6",
    "Grade 7",
    "Grade 8",
    "Grade 9",
    "Grade 10",
    "Grade 11",
    "Grade 12",
  ];

  // Helper function to get previous class
  const getPreviousClass = (currentClass, yearsBack = 1) => {
    const classProgression = [
      "PP1",
      "PP2",
      "Grade 1",
      "Grade 2",
      "Grade 3",
      "Grade 4",
      "Grade 5",
      "Grade 6",
      "Grade 7",
      "Grade 8",
      "Grade 9",
      "Grade 10",
      "Grade 11",
      "Grade 12",
    ];

    const currentIndex = classProgression.indexOf(currentClass);
    if (currentIndex === -1 || currentIndex - yearsBack < 0) {
      return null;
    }

    return classProgression[currentIndex - yearsBack];
  };

  // Helper function to get class for specific year based on current class
  const getClassForYear = (targetYear, currentYear, currentClass) => {
    const yearsDifference = parseInt(currentYear) - parseInt(targetYear);

    if (yearsDifference < 0) {
      // Future year - should not happen for past results
      return currentClass;
    }

    // Get the class based on years back
    const previousClass = getPreviousClass(currentClass, yearsDifference);

    // If we can't determine previous class (too far back), return earliest class
    return previousClass || getPreviousClass(currentClass, 1) || currentClass;
  };

  // Function to get next class
  const getNextClass = (currentClass) => {
    const classProgression = [
      "PP1",
      "PP2",
      "Grade 1",
      "Grade 2",
      "Grade 3",
      "Grade 4",
      "Grade 5",
      "Grade 6",
      "Grade 7",
      "Grade 8",
      "Grade 9",
      "Grade 10",
      "Grade 11",
      "Grade 12",
    ];

    const currentIndex = classProgression.indexOf(currentClass);
    if (currentIndex === -1 || currentIndex === classProgression.length - 1) {
      return null;
    }

    return classProgression[currentIndex + 1];
  };

  // Function to check if should graduate
  const shouldGraduateToAlumni = (currentClass) => {
    return currentClass === "Grade 9" || currentClass === "Grade 12";
  };

  // Function to fetch academic history
  const fetchAcademicHistory = async () => {
    try {
      const response = await axios.get(
        `https://kidsm.vercel.app/api/schools/learner/${learner?._id}/academic-history`
      );
      if (response.data.success) {
        setAcademicHistory(response.data.data.academicHistory);
      }
    } catch (error) {
      console.error("Error fetching academic history:", error);
    }
  };

  // Function to handle promotion
  const handlePromoteLearner = async () => {
    if (!promotionData.academicYear) {
      setSnackbar({
        open: true,
        message: "Please select academic year",
        severity: "error",
      });
      return;
    }

    setPromoting(true);
    try {
      let response;

      if (shouldGraduateToAlumni(learner.class)) {
        // Graduate to alumni
        response = await axios.post(
          "https://kidsm.vercel.app/api/schools/graduate-to-alumni",
          {
            learnerId: learner._id,
            graduationYear: promotionData.academicYear,
            finalGrade: promotionData.finalGrade,
            furtherEducation: promotionData.furtherEducation,
            currentOccupation: promotionData.currentOccupation,
            remarks: promotionData.remarks,
          }
        );
      } else {
        // Regular promotion
        const nextClass = getNextClass(learner.class);
        if (!nextClass) {
          setSnackbar({
            open: true,
            message: "Cannot determine next class for promotion",
            severity: "error",
          });
          return;
        }

        response = await axios.post(
          "https://kidsm.vercel.app/api/schools/promote-learner",
          {
            learnerId: learner._id,
            newClass: nextClass,
            newStream: learner.stream, // Keep same stream
            academicYear: promotionData.academicYear,
            remarks: promotionData.remarks,
          }
        );
      }

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "success",
        });

        // Refresh learner data
        const updatedLearner = await axios.get(
          `https://kidsm.vercel.app/api/schools/learner/${learner?._id}`
        );

        if (updatedLearner.data) {
          // Update the learner in state/navigation
          navigate(location.pathname, {
            state: { learner: updatedLearner.data },
          });
        }

        setShowPromotionModal(false);
        setPromotionStep(0);
        setPromotionData({
          academicYear: new Date().getFullYear().toString(),
          newClass: "",
          newStream: "",
          remarks: "",
          finalGrade: "",
          furtherEducation: {
            institution: "",
            course: "",
            yearStarted: "",
            qualification: "",
          },
          currentOccupation: {
            company: "",
            position: "",
            industry: "",
            yearStarted: "",
          },
        });
      }
    } catch (error) {
      console.error("Error promoting learner:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to promote learner",
        severity: "error",
      });
    } finally {
      setPromoting(false);
    }
  };

  // Function to render promotion modal steps
  const renderPromotionStep = () => {
    switch (promotionStep) {
      case 0: // Confirmation step
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Promotion Confirmation
            </Typography>
            <Typography variant="body1" paragraph>
              You are about to promote <strong>{learner?.fullName}</strong> from{" "}
              <strong>{learner?.class}</strong>
              {learner?.stream && ` (${learner.stream})`}.
            </Typography>

            {shouldGraduateToAlumni(learner?.class) ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                This learner is in a graduating class ({learner?.class}). They
                will be moved to the Alumni section.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Learner will be promoted to{" "}
                <strong>{getNextClass(learner?.class)}</strong>
                {learner?.stream && ` (${learner.stream})`}.
              </Alert>
            )}

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={promotionData.academicYear}
                onChange={(e) =>
                  setPromotionData({
                    ...promotionData,
                    academicYear: e.target.value,
                  })
                }
                label="Academic Year"
              >
                <MenuItem value={new Date().getFullYear().toString()}>
                  {new Date().getFullYear()}
                </MenuItem>
                <MenuItem value={(new Date().getFullYear() + 1).toString()}>
                  {new Date().getFullYear() + 1}
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Remarks (Optional)"
              value={promotionData.remarks}
              onChange={(e) =>
                setPromotionData({
                  ...promotionData,
                  remarks: e.target.value,
                })
              }
              margin="normal"
              multiline
              rows={2}
            />
          </Box>
        );

      case 1: // Graduation details (only for graduating classes)
        if (!shouldGraduateToAlumni(learner?.class)) {
          setPromotionStep(2);
          return null;
        }

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Graduation Details
            </Typography>

            <TextField
              fullWidth
              label="Final Grade (Optional)"
              value={promotionData.finalGrade}
              onChange={(e) =>
                setPromotionData({
                  ...promotionData,
                  finalGrade: e.target.value,
                })
              }
              margin="normal"
            />

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
              Further Education (Optional)
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Institution"
                  value={promotionData.furtherEducation.institution}
                  onChange={(e) =>
                    setPromotionData({
                      ...promotionData,
                      furtherEducation: {
                        ...promotionData.furtherEducation,
                        institution: e.target.value,
                      },
                    })
                  }
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Course"
                  value={promotionData.furtherEducation.course}
                  onChange={(e) =>
                    setPromotionData({
                      ...promotionData,
                      furtherEducation: {
                        ...promotionData.furtherEducation,
                        course: e.target.value,
                      },
                    })
                  }
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Year Started"
                  value={promotionData.furtherEducation.yearStarted}
                  onChange={(e) =>
                    setPromotionData({
                      ...promotionData,
                      furtherEducation: {
                        ...promotionData.furtherEducation,
                        yearStarted: e.target.value,
                      },
                    })
                  }
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Qualification"
                  value={promotionData.furtherEducation.qualification}
                  onChange={(e) =>
                    setPromotionData({
                      ...promotionData,
                      furtherEducation: {
                        ...promotionData.furtherEducation,
                        qualification: e.target.value,
                      },
                    })
                  }
                  margin="normal"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
              Current Occupation (Optional)
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company"
                  value={promotionData.currentOccupation.company}
                  onChange={(e) =>
                    setPromotionData({
                      ...promotionData,
                      currentOccupation: {
                        ...promotionData.currentOccupation,
                        company: e.target.value,
                      },
                    })
                  }
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Position"
                  value={promotionData.currentOccupation.position}
                  onChange={(e) =>
                    setPromotionData({
                      ...promotionData,
                      currentOccupation: {
                        ...promotionData.currentOccupation,
                        position: e.target.value,
                      },
                    })
                  }
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Industry"
                  value={promotionData.currentOccupation.industry}
                  onChange={(e) =>
                    setPromotionData({
                      ...promotionData,
                      currentOccupation: {
                        ...promotionData.currentOccupation,
                        industry: e.target.value,
                      },
                    })
                  }
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Year Started"
                  value={promotionData.currentOccupation.yearStarted}
                  onChange={(e) =>
                    setPromotionData({
                      ...promotionData,
                      currentOccupation: {
                        ...promotionData.currentOccupation,
                        yearStarted: e.target.value,
                      },
                    })
                  }
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2: // Review and confirm
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Promotion Details
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Learner</strong>
                    </TableCell>
                    <TableCell>{learner?.fullName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Current Class</strong>
                    </TableCell>
                    <TableCell>
                      {learner?.class}
                      {learner?.stream && ` (${learner.stream})`}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Action</strong>
                    </TableCell>
                    <TableCell>
                      {shouldGraduateToAlumni(learner?.class)
                        ? `Graduate to Alumni (${learner?.class})`
                        : `Promote to ${getNextClass(learner?.class)}`}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Academic Year</strong>
                    </TableCell>
                    <TableCell>{promotionData.academicYear}</TableCell>
                  </TableRow>
                  {promotionData.remarks && (
                    <TableRow>
                      <TableCell>
                        <strong>Remarks</strong>
                      </TableCell>
                      <TableCell>{promotionData.remarks}</TableCell>
                    </TableRow>
                  )}
                  {shouldGraduateToAlumni(learner?.class) &&
                    promotionData.finalGrade && (
                      <TableRow>
                        <TableCell>
                          <strong>Final Grade</strong>
                        </TableCell>
                        <TableCell>{promotionData.finalGrade}</TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="warning" sx={{ mb: 2 }}>
              This action cannot be undone. Please review all details before
              confirming.
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  // Component to show academic history
  const renderAcademicHistory = () => {
    if (!academicHistory || academicHistory.length === 0) {
      return (
        <Typography sx={{ color: "#666", fontStyle: "italic" }}>
          No academic history available.
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Academic Year</strong>
              </TableCell>
              <TableCell>
                <strong>Class</strong>
              </TableCell>
              <TableCell>
                <strong>Stream</strong>
              </TableCell>
              <TableCell>
                <strong>Final Grade</strong>
              </TableCell>
              <TableCell>
                <strong>Remarks</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {academicHistory.map((record, index) => (
              <TableRow key={index}>
                <TableCell>{record.academicYear}</TableCell>
                <TableCell>{record.class}</TableCell>
                <TableCell>{record.stream || "N/A"}</TableCell>
                <TableCell>{record.finalGrade || "N/A"}</TableCell>
                <TableCell>{record.remarks || "N/A"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Process results to organize by year - FIXED VERSION
  const processResultsByYear = useMemo(() => {
    if (!results || typeof results !== "object") return {};

    const yearClassMap = {};
    const currentYear = new Date().getFullYear().toString();
    const currentClass = learner?.class || editForm.className;

    // Get academic history from learner
    const academicHistory = learner?.academicHistory || [];

    // Create a map of year -> class from academic history
    const yearClassFromHistory = {};
    academicHistory.forEach((record) => {
      yearClassFromHistory[record.academicYear] = {
        class: record.class,
        stream: record.stream || null,
      };
    });

    // Also check promotion history for additional context
    const promotionHistory = learner?.promotionHistory || [];
    promotionHistory.forEach((record) => {
      if (record.academicYear && !yearClassFromHistory[record.academicYear]) {
        yearClassFromHistory[record.academicYear] = {
          class: record.fromClass, // Use the class they were promoted FROM
          stream: record.fromStream || null,
        };
      }
    });

    // Helper function to get class for a specific year
    const getClassForYear = (year) => {
      // If we have academic history for this year, use it
      if (yearClassFromHistory[year]) {
        return yearClassFromHistory[year];
      }

      // If not in history, calculate based on current class and year difference
      const currentYearNum = parseInt(currentYear);
      const targetYearNum = parseInt(year);
      const yearsDifference = currentYearNum - targetYearNum;

      // Get the appropriate class based on years difference
      let calculatedClass = currentClass;

      if (yearsDifference > 0) {
        // Go back one class for each year
        for (let i = 0; i < yearsDifference; i++) {
          const previousClass = getPreviousClass(calculatedClass, 1);
          if (previousClass) {
            calculatedClass = previousClass;
          } else {
            // If we can't go further back (e.g., at PP1), break
            break;
          }
        }
      }

      // Try to find the most recent class before this year
      const sortedYears = Object.keys(yearClassFromHistory).sort(
        (a, b) => parseInt(b) - parseInt(a)
      );
      for (const historyYear of sortedYears) {
        if (parseInt(historyYear) < targetYearNum) {
          // For years between known history years, calculate progression
          const historyClass = yearClassFromHistory[historyYear].class;
          const yearDiff = targetYearNum - parseInt(historyYear);
          let tempClass = historyClass;

          // Move forward one class for each year after the history year
          for (let i = 0; i < yearDiff; i++) {
            const nextClass = getNextClass(tempClass);
            if (nextClass) {
              tempClass = nextClass;
            }
          }
          return {
            class: tempClass,
            stream: yearClassFromHistory[historyYear].stream,
          };
        }
      }

      // Default to calculated class if no history found
      return {
        class: calculatedClass,
        stream: null,
      };
    };

    // If results is an array (structured results from backend)
    if (Array.isArray(results)) {
      results.forEach((result) => {
        // Extract year from result - check multiple possible fields
        let year = result.academicYear || result.year || currentYear;

        // If result has createdAt, try to extract year from it
        if (!year && result.createdAt) {
          year = new Date(result.createdAt).getFullYear().toString();
        }

        // Get the correct class for this year
        const classInfo = getClassForYear(year);
        const className = classInfo.class;
        const classStream = classInfo.stream;

        if (!yearClassMap[year]) {
          yearClassMap[year] = {
            year,
            class: className,
            stream: classStream,
            terms: {},
          };
        }

        // Process terms if they exist in the result
        if (result.terms && typeof result.terms === "object") {
          Object.entries(result.terms).forEach(([term, examTypes]) => {
            if (!yearClassMap[year].terms[term]) {
              yearClassMap[year].terms[term] = {};
            }

            if (examTypes && typeof examTypes === "object") {
              Object.entries(examTypes).forEach(([examType, subjects]) => {
                if (!Array.isArray(subjects)) {
                  // Convert to array if it's not
                  subjects = [subjects];
                }

                if (!yearClassMap[year].terms[term][examType]) {
                  yearClassMap[year].terms[term][examType] = [];
                }

                subjects.forEach((subjectData) => {
                  if (subjectData && typeof subjectData === "object") {
                    yearClassMap[year].terms[term][examType].push({
                      subject: subjectData.subject || "Unknown",
                      marks: subjectData.marks || 0,
                      className: subjectData.className || className, // Use the determined class
                    });
                  }
                });
              });
            }
          });
        }

        // Also handle flat results structure (if results are individual subject entries)
        if (result.term && result.subject) {
          const term = result.term;
          const examType = result.examType || "General Exam";
          const subject = result.subject;
          const marks = result.marks || 0;
          const classNameFromResult = result.className || className;

          if (!yearClassMap[year].terms[term]) {
            yearClassMap[year].terms[term] = {};
          }

          if (!yearClassMap[year].terms[term][examType]) {
            yearClassMap[year].terms[term][examType] = [];
          }

          // Check if subject already exists to avoid duplicates
          const existingIndex = yearClassMap[year].terms[term][
            examType
          ].findIndex((item) => item.subject === subject);

          if (existingIndex === -1) {
            yearClassMap[year].terms[term][examType].push({
              subject: subject,
              marks: marks,
              className: classNameFromResult,
            });
          } else {
            // Update existing entry if marks are different
            yearClassMap[year].terms[term][examType][existingIndex].marks =
              marks;
          }
        }
      });
    }
    // If results is an object with terms (old backend format)
    else if (results && typeof results === "object") {
      // Determine which year this result belongs to
      let year = results.year || results.academicYear || currentYear;

      // Get the correct class for this year
      const classInfo = getClassForYear(year);
      const className = classInfo.class;
      const classStream = classInfo.stream;

      if (!yearClassMap[year]) {
        yearClassMap[year] = {
          year,
          class: className,
          stream: classStream,
          terms: {},
        };
      }

      // Process each property - skip non-term properties
      Object.entries(results).forEach(([key, value]) => {
        // Skip if it's not a term (like year, _id, __v, etc.)
        if (
          key === "year" ||
          key === "academicYear" ||
          key === "_id" ||
          key === "__v" ||
          key === "createdAt" ||
          key === "updatedAt"
        ) {
          return;
        }

        // Check if this key looks like a term (Term 1, Term 2, etc.)
        if (key.startsWith("Term") || key.includes("term")) {
          const term = key;

          if (!yearClassMap[year].terms[term]) {
            yearClassMap[year].terms[term] = {};
          }

          if (value && typeof value === "object") {
            Object.entries(value).forEach(([examType, subjects]) => {
              if (!Array.isArray(subjects)) {
                // Convert to array if it's not
                subjects = [subjects];
              }

              if (!yearClassMap[year].terms[term][examType]) {
                yearClassMap[year].terms[term][examType] = [];
              }

              subjects.forEach((subjectData) => {
                if (subjectData && typeof subjectData === "object") {
                  yearClassMap[year].terms[term][examType].push({
                    subject: subjectData.subject || "Unknown",
                    marks: subjectData.marks || 0,
                    className: subjectData.className || className,
                  });
                }
              });
            });
          }
        }
      });
    }

    // Sort years in descending order
    const sortedYearClassMap = {};
    Object.keys(yearClassMap)
      .sort((a, b) => parseInt(b) - parseInt(a))
      .forEach((year) => {
        sortedYearClassMap[year] = yearClassMap[year];
      });

    return sortedYearClassMap;
  }, [results, learner, editForm.className]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resultsResponse, streamsResponse] = await Promise.all([
          axios.get(
            `https://kidsm.vercel.app/api/schools/learner/${learner?._id}/results`
          ),
          axios.get(`https://kidsm.vercel.app/api/schools/${schoolId}/streams`),
        ]);

        console.log("API Response:", resultsResponse.data);
        setResults(resultsResponse.data.results);

        // Expand current year by default
        const currentYear = new Date().getFullYear().toString();
        setExpandedYears({ [currentYear]: true });

        if (streamsResponse.data.classStreams) {
          setSchoolStreams(streamsResponse.data.classStreams);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setSnackbar({
          open: true,
          message: "Failed to load data",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    if (learner?._id && schoolId) fetchData();
    else {
      console.error("Missing learner ID or school ID");
      setSnackbar({
        open: true,
        message: "Missing learner ID or school ID",
        severity: "error",
      });
      setLoading(false);
    }
  }, [learner?._id, schoolId]);

  const handleDeleteStudent = async () => {
    const cleanedInput = birthCertInput.trim();
    if (!cleanedInput) {
      setSnackbar({
        open: true,
        message: "Please enter a birth certificate number",
        severity: "error",
      });
      return;
    }

    setDeleting(true);
    try {
      const response = await axios.post(
        "https://kidsm.vercel.app/api/schools/delete-student",
        {
          studentId: learner?._id,
          birthCertificateNumber: cleanedInput,
        }
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "success",
        });
        navigate(-1);
      } else {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting student:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to delete student",
        severity: "error",
      });
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleUpdateLearner = async () => {
    if (!schoolId) {
      setSnackbar({
        open: true,
        message: "School ID is missing. Please log in again.",
        severity: "error",
      });
      return;
    }

    const learnerData = { schoolId };
    if (editForm.fullName) learnerData.fullName = editForm.fullName;
    if (editForm.dateOfBirth) learnerData.dateOfBirth = editForm.dateOfBirth;
    if (editForm.className) learnerData.class = editForm.className;
    if (editForm.stream) learnerData.stream = editForm.stream;
    if (editForm.admissionNumber)
      learnerData.admissionNumber = editForm.admissionNumber;
    if (editForm.birthCertificateNumber)
      learnerData.birthCertificateNumber = editForm.birthCertificateNumber;
    if (editForm.gender) learnerData.gender = editForm.gender;
    if (editForm.parentName) learnerData.parentName = editForm.parentName;
    if (editForm.parentContact)
      learnerData.parentContact = editForm.parentContact;
    if (editForm.parentEmail) learnerData.parentEmail = editForm.parentEmail;
    if (editForm.parentId) learnerData.parentId = editForm.parentId;

    const hasStreams = schoolStreams[editForm.className]?.length > 0;
    if (hasStreams && !editForm.stream) learnerData.stream = null;

    setUpdating(true);
    try {
      const response = await axios.put(
        `https://kidsm.vercel.app/api/schools/learner/${learner?._id}`,
        learnerData
      );
      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: "Learner updated successfully!",
          severity: "success",
        });
        setShowEditModal(false);
        navigate(location.pathname, {
          state: { learner: response.data.learner },
        });
      }
    } catch (error) {
      console.error("Error updating learner:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update learner",
        severity: "error",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleYearToggle = (year) => {
    setExpandedYears((prev) => ({
      ...prev,
      [year]: !prev[year],
    }));
  };

  // Transform results for analysis
  const transformResultsToData = (term, examType, year, className) => {
    if (!results || !term) return null;

    const subjects = new Set();
    const marksData = {};

    try {
      // Handle different result structures
      const processedResults = processResultsByYear;
      const yearData = processedResults[year];

      if (!yearData || !yearData.terms[term]) return null;

      const examTypes = yearData.terms[term];

      if (examType) {
        const examData = examTypes[examType];
        if (!examData || !Array.isArray(examData)) return null;

        examData.forEach(({ subject, marks }) => {
          if (subject) {
            subjects.add(subject);
            marksData[subject] = marks || 0;
          }
        });
      } else {
        // All exam types for the term
        Object.values(examTypes).forEach((examData) => {
          if (Array.isArray(examData)) {
            examData.forEach(({ subject, marks }) => {
              if (subject) {
                subjects.add(subject);
                marksData[subject] = marks || 0;
              }
            });
          }
        });
      }

      const learnerData = {
        learnerName: learner?.fullName || "Unknown",
        stream: learner?.stream || "No Stream",
        class: className || learner?.class || editForm.className,
        year: year,
        marks: marksData,
      };

      return {
        learners: [learnerData],
        subjects: Array.from(subjects),
        year,
        class: className,
      };
    } catch (error) {
      console.error("Error transforming results:", error);
      return null;
    }
  };

  const renderResultsByYear = () => {
    const processedResults = processResultsByYear;
    const years = Object.keys(processedResults).sort().reverse();

    if (years.length === 0) {
      return (
        <Typography
          sx={{ color: "#666", fontStyle: "italic", p: 3, textAlign: "center" }}
        >
          No academic results found for this learner.
        </Typography>
      );
    }

    return years.map((year) => {
      const yearData = processedResults[year];
      const isCurrentYear = year === new Date().getFullYear().toString();
      const isExpanded = expandedYears[year] || false;

      return (
        <Accordion
          key={year}
          expanded={isExpanded}
          onChange={() => handleYearToggle(year)}
          sx={{
            mb: 2,
            backgroundColor: isCurrentYear ? "#f0f9ff" : "#fafafa",
            border: "1px solid",
            borderColor: isCurrentYear ? "#2196f3" : "#e0e0e0",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              minHeight: "60px",
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                gap: 2,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CalendarToday
                sx={{
                  color: isCurrentYear ? "#2196f3" : "#757575",
                  fontSize: "1.5rem",
                }}
              />
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: isCurrentYear ? "#1976d2" : "#333",
                  }}
                >
                  Academic Year {year}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: isCurrentYear ? "#1976d2" : "#666",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <School fontSize="small" />
                  {yearData.class}{" "}
                  {yearData.stream ? `• ${yearData.stream}` : ""}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}
            >
              {isCurrentYear && (
                <Chip
                  label="Current Year"
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              <Chip
                label={`${Object.keys(yearData.terms).length} Terms`}
                size="small"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 2, backgroundColor: "white" }}>
            {Object.keys(yearData.terms).length === 0 ? (
              <Typography
                sx={{
                  color: "#666",
                  fontStyle: "italic",
                  p: 3,
                  textAlign: "center",
                  border: "1px dashed #e0e0e0",
                  borderRadius: 1,
                }}
              >
                No results recorded for {year}
              </Typography>
            ) : (
              Object.entries(yearData.terms).map(([term, examTypes]) => (
                <Paper
                  key={term}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 3,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      pb: 1,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        color: "#333",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Assessment fontSize="small" />
                      {term}
                    </Typography>

                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<TrendingUp />}
                      onClick={() => {
                        const data = transformResultsToData(
                          term,
                          null,
                          year,
                          yearData.class
                        );
                        if (!data) {
                          setSnackbar({
                            open: true,
                            message: "No data available for analysis",
                            severity: "error",
                          });
                          return;
                        }
                        navigate("/analysis", {
                          state: {
                            term,
                            examType: null,
                            data,
                            schoolId,
                            grade: yearData.class,
                            year: year,
                          },
                        });
                      }}
                    >
                      Analyze Term
                    </Button>
                  </Box>

                  <Grid container spacing={2}>
                    {Object.entries(examTypes).map(([examType, subjects]) => {
                      // Ensure subjects is an array
                      const subjectsArray = Array.isArray(subjects)
                        ? subjects
                        : [subjects];

                      return (
                        <Grid item xs={12} md={6} key={examType}>
                          <Card variant="outlined" sx={{ height: "100%" }}>
                            <CardContent>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 2,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: "bold",
                                    color: "#555",
                                  }}
                                >
                                  {examType}
                                </Typography>

                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const data = transformResultsToData(
                                      term,
                                      examType,
                                      year,
                                      yearData.class
                                    );
                                    if (!data) {
                                      setSnackbar({
                                        open: true,
                                        message:
                                          "No data available for analysis",
                                        severity: "error",
                                      });
                                      return;
                                    }
                                    navigate("/analysis", {
                                      state: {
                                        term,
                                        examType,
                                        data,
                                        schoolId,
                                        grade: yearData.class,
                                        year: year,
                                      },
                                    });
                                  }}
                                >
                                  View Analysis
                                </Button>
                              </Box>

                              <Divider sx={{ my: 1 }} />

                              <Grid container spacing={1}>
                                {subjectsArray.map((subjectData, index) => {
                                  const subject =
                                    subjectData?.subject || "Unknown";
                                  const marks = subjectData?.marks || 0;

                                  return (
                                    <Grid item xs={6} sm={4} key={index}>
                                      <Paper
                                        elevation={0}
                                        sx={{
                                          p: 1.5,
                                          border: "1px solid #f0f0f0",
                                          borderRadius: 1,
                                          backgroundColor: "#fafafa",
                                          height: "100%",
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: "medium",
                                            color: "#333",
                                            textAlign: "center",
                                            mb: 0.5,
                                          }}
                                        >
                                          {subject}
                                        </Typography>
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            color: "#1976d2",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                          }}
                                        >
                                          {marks}
                                        </Typography>
                                      </Paper>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              ))
            )}
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  const renderAcademicSummary = () => {
    const processedResults = processResultsByYear;
    const years = Object.keys(processedResults).sort().reverse();

    if (years.length === 0) return null;

    // Get current year and current class
    const currentYear = new Date().getFullYear().toString();
    const currentClass = learner?.class || editForm.className;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: "#f8f9fa",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            color: "#333",
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <School /> Academic Summary
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "white",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="textSecondary">
                Total Academic Years
              </Typography>
              <Typography
                variant="h4"
                sx={{ color: "#1976d2", fontWeight: "bold" }}
              >
                {years.length}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "white",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Academic Progression
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {years.map((year, index) => {
                  // Calculate class for each year
                  let displayClass;

                  // If we have processed results with class info, use it
                  if (processedResults[year] && processedResults[year].class) {
                    displayClass = processedResults[year].class;
                  } else {
                    // Otherwise calculate based on current class
                    const yearsDifference =
                      parseInt(currentYear) - parseInt(year);
                    displayClass = currentClass;

                    // Go back one class for each year
                    for (let i = 0; i < yearsDifference; i++) {
                      const previousClass = getPreviousClass(displayClass, 1);
                      if (previousClass) {
                        displayClass = previousClass;
                      }
                    }
                  }

                  return (
                    <Chip
                      key={year}
                      label={`${year}: ${displayClass}`}
                      color={year === currentYear ? "primary" : "default"}
                      variant="outlined"
                      size="medium"
                      icon={<CalendarToday />}
                      sx={{ mb: 0.5 }}
                    />
                  );
                })}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  if (!learner || !schoolId) {
    return (
      <Container
        maxWidth="md"
        sx={{ py: 4, bgcolor: "#f5f5f5", minHeight: "100vh" }}
      >
        <Typography variant="h6" sx={{ textAlign: "center", color: "#e74c3c" }}>
          Error: Missing learner or school information
        </Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, minHeight: "100vh" }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "#333", mb: 1 }}
              >
                {learner?.fullName || "Unknown Learner"}
              </Typography>
              <Typography variant="subtitle1" sx={{ color: "#666", mb: 0.5 }}>
                Admission: {learner?.admissionNumber || "N/A"} • Birth Cert:{" "}
                {learner?.birthCertificateNumber || "N/A"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#1976d2", fontWeight: "medium" }}
              >
                Current: {learner?.class || editForm.className}
                {learner?.stream && ` • Stream: ${learner.stream}`}
              </Typography>
            </Box>

            <Box>
              <Chip
                label={`Academic Year ${new Date().getFullYear()}`}
                color="primary"
                variant="outlined"
                icon={<CalendarToday />}
                sx={{ fontWeight: "bold" }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Academic Summary */}
        {renderAcademicSummary()}

        {/* Results by Year */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: "#333",
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Assessment /> Academic Results by Year
          </Typography>

          {renderResultsByYear()}
        </Paper>

        {/* Student Information */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#333", mb: 2 }}
          >
            Student Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Parent Name
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {learner?.parentName || "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Parent Contact
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {learner?.parentContact || "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Parent Email
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {learner?.parentEmail || "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Date of Birth
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {learner?.dateOfBirth
                  ? new Date(learner.dateOfBirth).toLocaleDateString()
                  : "N/A"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Action Buttons */}
        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                bgcolor: "#FFC107",
                "&:hover": { bgcolor: "#FFB300" },
                py: 1.5,
              }}
              onClick={() => setShowEditModal(true)}
            >
              Edit Learner Details
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                bgcolor: "#4CAF50",
                "&:hover": { bgcolor: "#45A049" },
                py: 1.5,
              }}
              onClick={() =>
                navigate("/career-insights", {
                  state: { learnerId: learner?._id },
                })
              }
            >
              Career Insights
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                bgcolor: "#2196F3",
                "&:hover": { bgcolor: "#1E88E5" },
                py: 1.5,
              }}
              onClick={() => {
                if (!learner?.parentId || !learner?.schoolId) {
                  setSnackbar({
                    open: true,
                    message:
                      "Cannot Update Fees: Missing school or parent information. Contact support.",
                    severity: "error",
                  });
                  return;
                }
                navigate("/update-fee-balance", {
                  state: {
                    learner: {
                      ...learner,
                      _id: learner._id.toString(),
                      parentId: learner.parentId.toString(),
                      schoolId: learner.schoolId.toString(),
                      class: learner.class,
                    },
                  },
                });
              }}
            >
              Update Fee Balance
            </Button>
          </Grid>

          {/* Promotion Button */}
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                bgcolor: "#9C27B0",
                "&:hover": { bgcolor: "#7B1FA2" },
                py: 1.5,
              }}
              onClick={() => {
                setShowPromotionModal(true);
                fetchAcademicHistory();
              }}
              startIcon={<ArrowUpward />}
            >
              Promote Student
            </Button>
          </Grid>

          {/* Academic History Button */}
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                bgcolor: "#607D8B",
                "&:hover": { bgcolor: "#455A64" },
                py: 1.5,
              }}
              onClick={() => {
                setShowHistoryModal(true);
                fetchAcademicHistory();
              }}
              startIcon={<HistoryEdu />}
            >
              Academic History
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                bgcolor: "#FF9800",
                "&:hover": { bgcolor: "#FB8C00" },
                py: 1.5,
              }}
              onClick={() =>
                navigate("/report-indiscipline", {
                  state: {
                    learnerId: learner?._id,
                    learnerName: learner?.fullName || "Unknown",
                    schoolId: learner?.schoolId,
                  },
                })
              }
            >
              Report Indiscipline Case
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                bgcolor: "#F44336",
                "&:hover": { bgcolor: "#E53935" },
                py: 1.5,
              }}
              onClick={() => setShowDeleteModal(true)}
            >
              Delete Student
            </Button>
          </Grid>
        </Grid>

        {/* Edit Learner Modal */}
        <Dialog
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Learner Details</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Full Name"
              value={editForm.fullName}
              onChange={(e) =>
                setEditForm({ ...editForm, fullName: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Date of Birth (YYYY-MM-DD)"
              value={editForm.dateOfBirth}
              onChange={(e) =>
                setEditForm({ ...editForm, dateOfBirth: e.target.value })
              }
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Class</InputLabel>
              <Select
                value={editForm.className}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    className: e.target.value,
                    stream: "",
                  })
                }
                label="Class"
              >
                <MenuItem value="">Select Class</MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls} value={cls}>
                    {cls}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {schoolStreams[editForm.className]?.length > 0 && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Stream</InputLabel>
                <Select
                  value={editForm.stream}
                  onChange={(e) =>
                    setEditForm({ ...editForm, stream: e.target.value })
                  }
                  label="Stream"
                >
                  <MenuItem value="">Select Stream</MenuItem>
                  {schoolStreams[editForm.className].map((streamName) => (
                    <MenuItem key={streamName} value={streamName}>
                      {streamName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              fullWidth
              label="Admission Number"
              value={editForm.admissionNumber}
              onChange={(e) =>
                setEditForm({ ...editForm, admissionNumber: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Birth Certificate Number"
              value={editForm.birthCertificateNumber}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  birthCertificateNumber: e.target.value,
                })
              }
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Gender</InputLabel>
              <Select
                value={editForm.gender}
                onChange={(e) =>
                  setEditForm({ ...editForm, gender: e.target.value })
                }
                label="Gender"
              >
                <MenuItem value="">Select Gender</MenuItem>
                <MenuItem value="M">Male</MenuItem>
                <MenuItem value="F">Female</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Parent Name"
              value={editForm.parentName}
              onChange={(e) =>
                setEditForm({ ...editForm, parentName: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Parent Contact"
              value={editForm.parentContact}
              onChange={(e) =>
                setEditForm({ ...editForm, parentContact: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Parent Email"
              value={editForm.parentEmail}
              onChange={(e) =>
                setEditForm({ ...editForm, parentEmail: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Parent ID (auto-assigned)"
              value={editForm.parentId}
              disabled
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEditModal(false)} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleUpdateLearner}
              color="primary"
              disabled={updating}
            >
              {updating ? <CircularProgress size={24} /> : "Update Learner"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              To delete {learner?.fullName || "Unknown"}, please enter their
              birth certificate number:
            </Typography>
            <TextField
              fullWidth
              label="Birth Certificate Number"
              value={birthCertInput}
              onChange={(e) => setBirthCertInput(e.target.value)}
              autoCapitalize="none"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteModal(false)} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteStudent}
              color="error"
              disabled={deleting}
            >
              {deleting ? <CircularProgress size={24} /> : "Confirm Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Promotion Modal */}
        <Dialog
          open={showPromotionModal}
          onClose={() => {
            if (!promoting) {
              setShowPromotionModal(false);
              setPromotionStep(0);
            }
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ArrowUpward />
              Promote Student
            </Box>
          </DialogTitle>

          <DialogContent>
            <Stepper activeStep={promotionStep} sx={{ mb: 3 }}>
              <Step>
                <StepLabel>Confirmation</StepLabel>
              </Step>
              {shouldGraduateToAlumni(learner?.class) && (
                <Step>
                  <StepLabel>Graduation Details</StepLabel>
                </Step>
              )}
              <Step>
                <StepLabel>Review</StepLabel>
              </Step>
            </Stepper>

            {renderPromotionStep()}
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                if (promotionStep > 0) {
                  setPromotionStep(promotionStep - 1);
                } else {
                  setShowPromotionModal(false);
                  setPromotionStep(0);
                }
              }}
              disabled={promoting}
            >
              {promotionStep === 0 ? "Cancel" : "Back"}
            </Button>

            <Button
              onClick={() => {
                if (
                  promotionStep <
                  (shouldGraduateToAlumni(learner?.class) ? 2 : 1)
                ) {
                  setPromotionStep(promotionStep + 1);
                } else {
                  handlePromoteLearner();
                }
              }}
              variant="contained"
              color="primary"
              disabled={promoting}
            >
              {promotionStep ===
              (shouldGraduateToAlumni(learner?.class) ? 2 : 1) ? (
                promoting ? (
                  <CircularProgress size={24} />
                ) : (
                  "Confirm Promotion"
                )
              ) : (
                "Next"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Academic History Modal */}
        <Dialog
          open={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HistoryEdu />
              Academic History - {learner?.fullName}
            </Box>
          </DialogTitle>

          <DialogContent>{renderAcademicHistory()}</DialogContent>

          <DialogActions>
            <Button onClick={() => setShowHistoryModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
          sx={{
            bgcolor: snackbar.severity === "error" ? "#e74c3c" : "#4CAF50",
          }}
        />
      </motion.div>
    </Container>
  );
};

export default StudentDetails;
